import { useEffect, useState } from 'react'
import { TWS_API_URL } from '../../environment/apis.config'
import { getAccessToken } from '../../utils/auth.util'
import VideoPlayer from '../VideoPlayer/VideoPlayer'
import { FetchAllVideoFilesResponse, VideoFileObject } from './IpCameraTypes'
import Spinner from '../Spinner/Spinner'
import MainNavMenu from '../../menus/MainNavMenu/MainNavMenu'

interface PresignedUrlApiData {
  success: boolean
  url: string
}

const VideoList = () => {
  const [videoFiles, setVideoFiles] = useState<VideoFileObject[] | null>(null)
  const [videoSrc, setVideoSrc] = useState<string | null>(null)
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [startTime, setStartTime] = useState('')
  const [endTime, setEndTime] = useState('')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const [maxPageLength, setMaxPageLength] = useState(10)
  const [curPageNo, setCurPageNo] = useState(0)
  const [pageLimit, setPageLimit] = useState(0)

  const getAllIpCameraVideos = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessToken()
      const response: Response = await fetch(`${TWS_API_URL}/api/video/list`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to fetch videos')
      }

      const data: FetchAllVideoFilesResponse = await response.json()

      if (!data.success) {
        throw new Error('API returned unsuccessful response')
      }

      setVideoFiles(data.files)
      setPageLimit(Math.ceil(data.files.length / maxPageLength))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load videos')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAllIpCameraVideos()
  }, [])

  // Extract unique times from video files
  const getUniqueTimestamps = (): string[] => {
    if (!videoFiles) return []

    const times = new Set<string>()

    videoFiles.forEach((video) => {
      try {
        const filename = video.key.split('/')[1].split('.')[0]
        const [dateStr, timeStr] = filename.split('-')

        const hour = timeStr.substring(0, 2)
        const minute = timeStr.substring(2, 4)
        const second = timeStr.substring(4, 6)

        const year = 2000 + parseInt(dateStr.substring(0, 2))
        const month = dateStr.substring(2, 4)
        const day = dateStr.substring(4, 6)
        times.add(`${month}/${day}/${year} ${hour}:${minute}:${second}`)
      } catch (err) {
        // Skip malformed filenames
      }
    })

    return Array.from(times).sort()
  }

  const uniqueTimestamps = getUniqueTimestamps()

  const fetchVideoPreSignedUrl = async (key: string) => {
    setLoading(true)
    setError(null)
    try {
      const token = await getAccessToken()
      const response: Response = await fetch(
        `${TWS_API_URL}/api/video/${key.split('/')[1].split('.')[0]}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )

      if (!response.ok) {
        throw new Error('Failed to fetch video URL')
      }

      const data: PresignedUrlApiData = await response.json()

      if (!data.success) {
        throw new Error('Failed to generate video URL')
      }

      setVideoSrc(data.url)
      setSelectedVideo(key)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load video')
    } finally {
      setLoading(false)
    }
  }

  const parseFilenameToDate = (filename: string): string => {
    try {
      filename = filename.split('/')[1]
      filename = filename.split('.')[0]
      const [dateStr, timeStr] = filename.split('-')

      const year = 2000 + parseInt(dateStr.substring(0, 2))
      const month = parseInt(dateStr.substring(2, 4)) - 1
      const day = parseInt(dateStr.substring(4, 6))
      const hour = parseInt(timeStr.substring(0, 2))
      const minute = parseInt(timeStr.substring(2, 4))
      const second = parseInt(timeStr.substring(4, 6))

      const date = new Date(year, month, day, hour, minute, second)

      // Format with leading zeros
      const formatNum = (num: number) => num.toString().padStart(2, '0')

      return `${formatNum(date.getMonth() + 1)}/${formatNum(
        date.getDate()
      )}/${date.getFullYear()} ${formatNum(date.getHours())}:${formatNum(
        date.getMinutes()
      )}:${formatNum(date.getSeconds())}`
    } catch (err) {
      return filename
    }
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
  }

  const getFilteredAndSortedVideos = () => {
    if (!videoFiles) return []

    let filtered = videoFiles

    let filterStartTime = startTime.split(' ')[1]
    let filterEndTime = endTime.split(' ')[1]

    // Filter by time range if specified
    if (filterStartTime || filterEndTime) {
      filtered = filtered.filter((video) => {
        try {
          const filename = video.key.split('/')[1].split('.')[0]
          const [dateStr, timeStr] = filename.split('-')

          const hour = timeStr.substring(0, 2)
          const minute = timeStr.substring(2, 4)
          const second = timeStr.substring(4, 6)

          const videoTime = `${hour}:${minute}:${second}`

          // Compare timestamps as strings (works since format is HH:MM:SS)
          if (filterStartTime && filterEndTime) {
            return videoTime >= filterStartTime && videoTime <= filterEndTime
          } else if (filterStartTime) {
            return videoTime >= filterStartTime
          } else if (filterEndTime) {
            return videoTime <= filterEndTime
          }

          return true
        } catch (err) {
          return false
        }
      })
    }

    filtered.sort((a, b) => {
      const dateA = parseFilenameToDate(a.key)
      const dateB = parseFilenameToDate(b.key)
      return sortOrder === 'desc'
        ? dateB.localeCompare(dateA)
        : dateA.localeCompare(dateB)
    })

    return filtered
  }

    const filteredVideos = getFilteredAndSortedVideos()
    const paginatedVideos = filteredVideos.slice(
      curPageNo * maxPageLength,
      (curPageNo + 1) * maxPageLength
    )
    const totalPages = Math.ceil(filteredVideos.length / maxPageLength)

  return (
    <div className="flex w-full bg-gray-50">
      <div>
        <MainNavMenu />
      </div>
      {/* Sidebar - Video List */}
      <div className="w-1/2 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            IP Camera Videos
          </h2>

          {/* Time Range Filter */}
          <div className="space-y-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Start Time
              </label>
              <select
                value={startTime}
                onChange={(e) => {
                  setStartTime(e.target.value)
                  setCurPageNo(0)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
              >
                <option value="">All times</option>
                {uniqueTimestamps.map((time) => (
                  <option key={`start-${time}`} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                End Time
              </label>
              <select
                value={endTime}
                onChange={(e) => {
                  setEndTime(e.target.value)
                  setCurPageNo(0)
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
              >
                <option value="">All times</option>
                {uniqueTimestamps.map((time) => (
                  <option key={`end-${time}`} value={time}>
                    {time}
                  </option>
                ))}
              </select>
            </div>

            {(startTime || endTime) && (
              <button
                onClick={() => {
                  setStartTime('')
                  setEndTime('')
                  setCurPageNo(0)
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Clear Filters
              </button>
            )}
          </div>

          {/* Controls */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={
                    sortOrder === 'asc'
                      ? 'M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12'
                      : 'M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4'
                  }
                />
              </svg>
              {sortOrder === 'asc' ? 'Oldest' : 'Newest'}
            </button>

            <button
              onClick={getAllIpCameraVideos}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>

          {/* Results count */}
          <div className="mt-3 text-sm text-gray-500">
            {filteredVideos?.length} video
            {filteredVideos?.length !== 1 ? 's' : ''}
          </div>
        </div>

        {/* Video List */}
        <div className="flex-1 overflow-y-auto">
          {loading && !videoFiles && (
            <div className="flex items-center justify-center h-64">
              <Spinner message="Loading videos..." />
            </div>
          )}

          {error && (
            <div className="m-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {!loading && filteredVideos?.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-gray-400">
              <svg
                className="w-16 h-16 mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              <p className="text-sm">No videos found</p>
            </div>
          )}

          {paginatedVideos?.map((video) => (
            <div
              key={video.key}
              onClick={() => fetchVideoPreSignedUrl(video.key)}
              className={`p-4 border-b border-gray-100 cursor-pointer transition-colors hover:bg-blue-50 ${
                selectedVideo === video.key
                  ? 'bg-blue-50 border-l-4 border-l-blue-500'
                  : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <svg
                      className="w-4 h-4 text-gray-400 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {parseFilenameToDate(video.key)}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 ml-6">
                    {formatFileSize(video.size)}
                  </p>
                </div>
                {selectedVideo === video.key && (
                  <svg
                    className="w-5 h-5 text-blue-500 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurPageNo(Math.max(0, curPageNo - 1))}
                disabled={curPageNo === 0}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <span className="text-sm text-gray-600">
                Page {curPageNo + 1} of {totalPages}
              </span>

              <button
                onClick={() =>
                  setCurPageNo(Math.min(totalPages - 1, curPageNo + 1))
                }
                disabled={curPageNo >= totalPages - 1}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content - Video Player */}
      <div className="flex-1 flex flex-col bg-gray-900">
        {loading && videoSrc && (
          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-10">
            <Spinner message="Loading video..." />
          </div>
        )}

        {!videoSrc && !loading && (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <svg
              className="w-24 h-24 mb-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <h3 className="text-xl font-medium mb-2">No Video Selected</h3>
            <p className="text-sm">
              Select a video from the list to start playback
            </p>
          </div>
        )}

        {videoSrc && !loading && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="w-full max-w-6xl">
              <video
                className="w-full rounded-lg shadow-2xl"
                controls
                autoPlay
                key={videoSrc}
              >
                <source src={videoSrc} type="video/mp4" />
                Your browser does not support the video tag.
              </video>

              {selectedVideo && (
                <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                  <p className="text-white text-sm">
                    <span className="font-medium">Currently playing:</span>{' '}
                    {parseFilenameToDate(selectedVideo)}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VideoList
